import { Coords, DoneCallback, GridLayer, TileEvent, TileLayer, TileLayerOptions, Util } from 'leaflet';


const BASE_URL = 'https://{s}.arcgisonline.com/ArcGIS/rest/services';
const SUBDOMAINS = ['server'];


export class EsriTileLayer extends TileLayer {

    private metadataUrl: string;

    _url: string;

    constructor(imageSet: 'World_Imagery', options: TileLayerOptions) {
        super(`${BASE_URL}/${imageSet}/MapServer/tile/{z}/{y}/{x}`, options);
        this.metadataUrl = `${BASE_URL}/${imageSet}/MapServer/tilemap/{z}/{y}/{x}/{width}/{height}?f=json`;
    }

    createTile(coords: Coords, done: DoneCallback): HTMLElement {
        const tile = document.createElement('div');
        tile.style.overflow = 'hidden';
        const img = document.createElement('img');

        img.onload = () => done(undefined, tile);
        img.onerror = () => this._tileOnError(done, tile, new Error('image load failed'));
        img.alt = '';

        this.getTileImageUrl(coords)
            .then(({ url, x, y, z }) => {
                const scale = 2 ** (coords.z - z);
                img.style.width = `${256 * scale}px`;
                img.style.height = `${256 * scale}px`;
                img.style.transform = `translate(-${(coords.x - x * scale) * 256}px, -${(coords.y - y * scale) * 256}px)`;
                img.src = url;
            })
            .catch(error => this._tileOnError(done, img, error));

        tile.appendChild(img);
        return tile;
    }

    private async getTileImageUrl(coords: Coords) {
        let x = coords.x;
        let y = coords.y;
        let z = coords.z;
        let valid = false;
        while (!valid && z > 1) {
            try {
                valid = await this.isTileValid(x, y, z);
            } catch (error) {
                console.error(error);
                valid = false;
            }
            if (valid) {
                return {
                    url: Util.template(this._url, Util.extend({ s: this.getSubdomain(x, y), x, y, z }, this.options)),
                    x,
                    y,
                    z,
                };
            }
            z = z - 1;
            x = Math.floor(x / 2);
            y = Math.floor(y / 2);
        }
        throw Error('invalid tile');
    }

    private tilesValidCache: Record<string, Promise<boolean>> = {};

    private async isTileValid(x: number, y: number, z: number) {
        const cacheKey = `${x}:${y}:${z}`;
        if (!this.tilesValidCache[cacheKey]) {
            /*
            const center = this._map.getCenter();
            const pixelBounds = this['_getTiledPixelBounds'](center);
            const tileRange = this['_pxBoundsToTileRange'](pixelBounds);

            const startX = Math.min(tileRange.min.x, x);
            const startY = Math.min(tileRange.min.y, y);
            const width = Math.max(tileRange.max.x, x) - startX + 1;
            const height = Math.max(tileRange.max.y, y) - startY + 1;
            */
            const size = 8;

            const startX = Math.max(0, Math.floor(x / size) * size);
            const startY = Math.max(0, Math.floor(y / size) * size);
            const width = size;
            const height = size;

            const data = {
                x: startX,
                y: startY,
                z,
                width,
                height,
                s: this.getSubdomain(x, y),
            };
            const request = fetch(Util.template(this.metadataUrl, data)).then(r => r.json());
            for (let i = 0; i < width; i++) {
                for (let j = 0; j < height; j++) {
                    const cacheKey = `${startX + i}:${startY + j}:${z}`;
                    this.tilesValidCache[cacheKey] = request.then(r => {
                        if (i < r.location.width && j < r.location.height) {
                            return r.data[j * width + i];
                        } else {
                            return 0;
                        }
                    });
                }
            }
        }
        return this.tilesValidCache[cacheKey];
    }

    private getSubdomain(x: number, y: number) {
        return SUBDOMAINS[Math.abs(x + y) % SUBDOMAINS.length];
    }

    private getTileImage(tile: HTMLElement) {
        return tile.firstElementChild as HTMLImageElement;
    }

    private cleanImageHandlers(img: HTMLImageElement) {
        img.onload = null;
        img.onerror = null;
    }

    _onTileRemove(e: TileEvent) {
        this.cleanImageHandlers(this.getTileImage(e.tile));
    }

    _abortLoading() {
        for (const i in this._tiles) {
            if (this._tiles[i].coords.z !== this._tileZoom) {
                const tile = this._tiles[i];
                const img = this.getTileImage(tile.el);
                this.cleanImageHandlers(img);
                if (!img.complete) {
                    tile.el.remove();
                    delete this._tiles[i];
                    // @event tileabort: TileEvent
                    // Fired when a tile was loading but is now not wanted.
                    this.fire('tileabort', { tile: tile.el, coords: tile.coords });
                }
            }
        }
    }

    _tileReady(coords: Coords, err: Error, tile: HTMLElement) {
        // @ts-ignore
        return GridLayer.prototype['_tileReady'].call(this, coords, err, tile);
    }
}
